import React, { useEffect, useState, useRef } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Save, LogOut, ExternalLink, Plus, Trash2, Upload, Image as ImageIcon,
  Settings2, Wrench, LayoutGrid, Loader2, Star, MessageSquareQuote,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ICON_OPTIONS = [
  "cog", "disc", "power", "settings", "circle-stop", "battery-charging",
  "circle-dot", "activity", "zap", "truck", "droplet", "wrench",
];

const TABS = [
  { id: "general", label: "General", icon: Settings2 },
  { id: "services", label: "Services", icon: Wrench },
  { id: "reviews", label: "Reviews", icon: MessageSquareQuote },
  { id: "gallery", label: "Gallery", icon: LayoutGrid },
];

function Text({ label, value, onChange, textarea, ...rest }) {
  const Comp = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="font-mono2 text-[11px] uppercase text-neutral-500 block mb-1.5">{label}</span>
      <Comp
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={textarea ? 3 : undefined}
        className="w-full bg-black border border-white/15 px-3 py-2.5 text-white text-sm font-body focus:outline-none focus:border-primary-red"
        {...rest}
      />
    </label>
  );
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("general");
  const [saving, setSaving] = useState(false);
  const logoInput = useRef();
  const heroInput = useRef();
  const galleryInput = useRef();
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => setContent(data)).catch(() => toast.error("Failed to load content"));
  }, []);

  if (ready && !user) return <Navigate to="/admin/login" replace />;
  if (!content) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-primary-red font-display text-2xl">
        <Loader2 className="animate-spin mr-3" /> Loading
      </div>
    );
  }

  const set = (key, val) => setContent((c) => ({ ...c, [key]: val }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/content", content);
      setContent(data);
      toast.success("Changes saved");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadFile = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      set(field, url);
      toast.success("Image uploaded — remember to Save");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addGallery = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setContent((c) => ({
        ...c,
        gallery: [...(c.gallery || []), { id: crypto.randomUUID(), url, caption_en: "", caption_es: "" }],
      }));
      toast.success("Image added — remember to Save");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const updateService = (i, key, val) =>
    setContent((c) => {
      const s = [...c.services];
      s[i] = { ...s[i], [key]: val };
      return { ...c, services: s };
    });
  const addService = () =>
    setContent((c) => ({
      ...c,
      services: [...c.services, { id: crypto.randomUUID(), icon: "wrench", name_en: "New Service", name_es: "Nuevo Servicio", desc_en: "", desc_es: "" }],
    }));
  const removeService = (i) => setContent((c) => ({ ...c, services: c.services.filter((_, idx) => idx !== i) }));

  const updateReview = (i, key, val) =>
    setContent((c) => {
      const r = [...(c.reviews || [])];
      r[i] = { ...r[i], [key]: val };
      return { ...c, reviews: r };
    });
  const addReview = () =>
    setContent((c) => ({
      ...c,
      reviews: [...(c.reviews || []), { id: crypto.randomUUID(), name: "Customer Name", location: "", rating: 5, quote_en: "", quote_es: "" }],
    }));
  const removeReview = (i) => setContent((c) => ({ ...c, reviews: (c.reviews || []).filter((_, idx) => idx !== i) }));

  const updateGallery = (i, key, val) =>
    setContent((c) => {
      const g = [...c.gallery];
      g[i] = { ...g[i], [key]: val };
      return { ...c, gallery: g };
    });
  const removeGallery = (i) => setContent((c) => ({ ...c, gallery: c.gallery.filter((_, idx) => idx !== i) }));

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="JJ" className="h-9 w-auto object-contain" />
            <span className="font-display uppercase text-lg hidden sm:block">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" target="_blank" className="flex items-center gap-1.5 text-sm text-neutral-300 hover:text-white font-body" data-testid="view-site">
              <ExternalLink size={16} /> <span className="hidden sm:inline">View Site</span>
            </Link>
            <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-primary-red text-white font-body font-bold uppercase text-sm px-5 py-2.5 hover:bg-[#FF1E16] transition-colors disabled:opacity-60" data-testid="save-btn">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
            </button>
            <button onClick={logout} className="flex items-center gap-1.5 text-sm text-neutral-400 hover:text-primary-red font-body" data-testid="logout-btn">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8 grid md:grid-cols-[200px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="flex md:flex-col gap-2">
          {TABS.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              className={`flex items-center gap-2.5 px-4 py-3 font-body font-semibold uppercase text-sm border transition-colors ${
                tab === tb.id ? "bg-primary-red border-primary-red text-white" : "border-white/10 text-neutral-400 hover:text-white"
              }`}
              data-testid={`tab-${tb.id}`}
            >
              <tb.icon size={18} /> {tb.label}
            </button>
          ))}
        </aside>

        {/* Content */}
        <main>
          {uploading && (
            <div className="mb-4 flex items-center gap-2 text-primary-red font-mono2 text-xs uppercase">
              <Loader2 size={14} className="animate-spin" /> Uploading...
            </div>
          )}

          {tab === "general" && (
            <div className="space-y-8" data-testid="panel-general">
              <section className="border border-white/10 p-6 space-y-4">
                <h2 className="font-display uppercase text-xl">Brand & Hero</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Text label="Business Name" value={content.business_name} onChange={(v) => set("business_name", v)} data-testid="f-business-name" />
                  <div className="grid grid-cols-2 gap-4">
                    <Text label="Tagline (EN)" value={content.tagline_en} onChange={(v) => set("tagline_en", v)} />
                    <Text label="Tagline (ES)" value={content.tagline_es} onChange={(v) => set("tagline_es", v)} />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <span className="font-mono2 text-[11px] uppercase text-neutral-500 block">Logo</span>
                    <div className="flex items-center gap-3">
                      <img src={content.logo_url} alt="logo" className="h-12 w-auto object-contain bg-black border border-white/10 p-1" />
                      <button onClick={() => logoInput.current.click()} className="flex items-center gap-2 border border-white/15 px-3 py-2 text-sm hover:border-primary-red" data-testid="upload-logo">
                        <Upload size={14} /> Upload
                      </button>
                      <input ref={logoInput} type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e, "logo_url")} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <span className="font-mono2 text-[11px] uppercase text-neutral-500 block">Hero Background</span>
                    <div className="flex items-center gap-3">
                      <img src={content.hero_bg} alt="hero" className="h-12 w-20 object-cover border border-white/10" />
                      <button onClick={() => heroInput.current.click()} className="flex items-center gap-2 border border-white/15 px-3 py-2 text-sm hover:border-primary-red" data-testid="upload-hero">
                        <Upload size={14} /> Upload
                      </button>
                      <input ref={heroInput} type="file" accept="image/*" hidden onChange={(e) => handleImageUpload(e, "hero_bg")} />
                    </div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Text label="Hero Title (EN)" value={content.hero_title_en} onChange={(v) => set("hero_title_en", v)} textarea />
                  <Text label="Hero Title (ES)" value={content.hero_title_es} onChange={(v) => set("hero_title_es", v)} textarea />
                  <Text label="Hero Subtitle (EN)" value={content.hero_subtitle_en} onChange={(v) => set("hero_subtitle_en", v)} textarea />
                  <Text label="Hero Subtitle (ES)" value={content.hero_subtitle_es} onChange={(v) => set("hero_subtitle_es", v)} textarea />
                </div>
              </section>

              <section className="border border-white/10 p-6 space-y-4">
                <h2 className="font-display uppercase text-xl">Contact & Hours</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Text label="Phone" value={content.phone} onChange={(v) => set("phone", v)} data-testid="f-phone" />
                  <Text label="Public Email" value={content.email} onChange={(v) => set("email", v)} />
                  <Text label="WhatsApp Number (digits + country code, e.g. 19736341088)" value={content.whatsapp} onChange={(v) => set("whatsapp", v)} data-testid="f-whatsapp" />
                  <Text label="Quote Notification Email (where form requests are sent)" value={content.notification_email} onChange={(v) => set("notification_email", v)} data-testid="f-notif-email" />
                  <Text label="Address" value={content.address} onChange={(v) => set("address", v)} />
                  <Text label="City / State / Zip" value={content.city} onChange={(v) => set("city", v)} />
                  <Text label="Hours (EN)" value={content.hours_en} onChange={(v) => set("hours_en", v)} />
                  <Text label="Hours (ES)" value={content.hours_es} onChange={(v) => set("hours_es", v)} />
                </div>
              </section>

              <section className="border border-white/10 p-6 space-y-4">
                <h2 className="font-display uppercase text-xl">About</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Text label="About (EN)" value={content.about_en} onChange={(v) => set("about_en", v)} textarea />
                  <Text label="About (ES)" value={content.about_es} onChange={(v) => set("about_es", v)} textarea />
                </div>
              </section>
            </div>
          )}

          {tab === "services" && (
            <div className="space-y-4" data-testid="panel-services">
              <div className="flex justify-between items-center">
                <h2 className="font-display uppercase text-xl">Services ({content.services.length})</h2>
                <button onClick={addService} className="flex items-center gap-2 bg-white/10 px-4 py-2 text-sm font-body font-bold uppercase hover:bg-white/20" data-testid="add-service">
                  <Plus size={16} /> Add
                </button>
              </div>
              {content.services.map((s, i) => (
                <div key={s.id || i} className="border border-white/10 p-4 grid sm:grid-cols-[120px_1fr_1fr_auto] gap-3 items-start" data-testid={`service-row-${i}`}>
                  <label className="block">
                    <span className="font-mono2 text-[11px] uppercase text-neutral-500 block mb-1.5">Icon</span>
                    <select value={s.icon} onChange={(e) => updateService(i, "icon", e.target.value)} className="w-full bg-black border border-white/15 px-2 py-2.5 text-white text-sm">
                      {ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </label>
                  <div className="space-y-2">
                    <Text label="Name (EN)" value={s.name_en} onChange={(v) => updateService(i, "name_en", v)} />
                    <Text label="Desc (EN)" value={s.desc_en} onChange={(v) => updateService(i, "desc_en", v)} />
                  </div>
                  <div className="space-y-2">
                    <Text label="Name (ES)" value={s.name_es} onChange={(v) => updateService(i, "name_es", v)} />
                    <Text label="Desc (ES)" value={s.desc_es} onChange={(v) => updateService(i, "desc_es", v)} />
                  </div>
                  <button onClick={() => removeService(i)} className="text-neutral-500 hover:text-primary-red p-2 mt-5" data-testid={`del-service-${i}`}>
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {tab === "reviews" && (
            <div className="space-y-4" data-testid="panel-reviews">
              <div className="flex justify-between items-center">
                <h2 className="font-display uppercase text-xl">Reviews ({(content.reviews || []).length})</h2>
                <button onClick={addReview} className="flex items-center gap-2 bg-white/10 px-4 py-2 text-sm font-body font-bold uppercase hover:bg-white/20" data-testid="add-review">
                  <Plus size={16} /> Add
                </button>
              </div>
              {(content.reviews || []).map((r, i) => (
                <div key={r.id || i} className="border border-white/10 p-4 space-y-3" data-testid={`review-row-${i}`}>
                  <div className="grid sm:grid-cols-[1fr_1fr_120px_auto] gap-3 items-end">
                    <Text label="Name" value={r.name} onChange={(v) => updateReview(i, "name", v)} />
                    <Text label="Location / Role" value={r.location} onChange={(v) => updateReview(i, "location", v)} />
                    <label className="block">
                      <span className="font-mono2 text-[11px] uppercase text-neutral-500 block mb-1.5">Rating</span>
                      <select value={r.rating} onChange={(e) => updateReview(i, "rating", parseInt(e.target.value))} className="w-full bg-black border border-white/15 px-2 py-2.5 text-white text-sm">
                        {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
                      </select>
                    </label>
                    <button onClick={() => removeReview(i)} className="text-neutral-500 hover:text-primary-red p-2" data-testid={`del-review-${i}`}>
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Text label="Quote (EN)" value={r.quote_en} onChange={(v) => updateReview(i, "quote_en", v)} textarea />
                    <Text label="Quote (ES)" value={r.quote_es} onChange={(v) => updateReview(i, "quote_es", v)} textarea />
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "gallery" && (
            <div className="space-y-4" data-testid="panel-gallery">
              <div className="flex justify-between items-center">
                <h2 className="font-display uppercase text-xl">Gallery ({content.gallery.length})</h2>
                <button onClick={() => galleryInput.current.click()} className="flex items-center gap-2 bg-primary-red px-4 py-2 text-sm font-body font-bold uppercase hover:bg-[#FF1E16]" data-testid="add-gallery">
                  <Upload size={16} /> Upload Image
                </button>
                <input ref={galleryInput} type="file" accept="image/*" hidden onChange={addGallery} />
              </div>
              {content.gallery.length === 0 ? (
                <div className="border border-white/10 p-16 text-center text-neutral-500 flex flex-col items-center gap-3">
                  <ImageIcon size={32} /> No images yet.
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {content.gallery.map((g, i) => (
                    <div key={g.id || i} className="border border-white/10 overflow-hidden" data-testid={`gallery-item-${i}`}>
                      <img src={g.url} alt="" className="w-full h-40 object-cover" />
                      <div className="p-3 space-y-2">
                        <Text label="Caption (EN)" value={g.caption_en} onChange={(v) => updateGallery(i, "caption_en", v)} />
                        <Text label="Caption (ES)" value={g.caption_es} onChange={(v) => updateGallery(i, "caption_es", v)} />
                        <button onClick={() => removeGallery(i)} className="flex items-center gap-1.5 text-primary-red text-sm hover:underline" data-testid={`del-gallery-${i}`}>
                          <Trash2 size={14} /> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
